/*****************************************************************************/
// VoxelGrid
// A 3D (or optionally 2D) voxel grid that allows for fast O(1) storage 
// and lookup of arbitrary objects by a specific point with a radius or by a 
// line segment (i.e. all objects that may intersect that line).
/*****************************************************************************/
import CoreUtils from '../../utils/coreUtils.js';
import Utils3D from '../utils/utils3D.js';

//const VOXEL_UNIQUE_ID_FIELD = '___vgrid_id___';
const VOXEL_UNIQUE_ID_FIELD = 999;
let iVoxelIdCounter = 0;

export default class VoxelGrid
{
/*****************************************************************************/
constructor(vecMinPoint, vecMaxPoint, iOptNumSideSubdivs = 16, bIs2D = false)
{
    let iVerticalSubdivs = bIs2D ? 1 : iOptNumSideSubdivs;

    // Allocate our grid:
    this.iNumVerticalSubdivs = iVerticalSubdivs;
    this.iNumSubdivs = iOptNumSideSubdivs;
	this.grid = null;    
    this.vecMin = vecMinPoint.clone();
    this.vecMax = vecMaxPoint.clone();
    this.vecSize = this.vecMax.subtract(this.vecMin);

	this.uniqueFieldName = null;

	// Address cases of zero size, because otherwise we enter infinite loops
	// when adding objects:
	if(this.vecSize.x < 1e-4)
	{
		this.vecMin.x -= 0.5;
		this.vecMax.x += 0.5;
	}
	if(this.vecSize.y < 1e-4)
	{
		this.vecMin.y -= 0.5;
		this.vecMax.y += 0.5;
	}
	if(this.vecSize.z < 1e-4)
	{
		this.vecMin.z -= 0.5;
		this.vecMax.z += 0.5;
	}

    this.vecBoxSize = this.vecSize.scale(1.0/this.iNumSubdivs);
    if(bIs2D)
    	this.vecBoxSize.y = this.vecSize.y;

	this.clear();
}
/*****************************************************************************/
setUniqueFieldName(strName)
{
	this.uniqueFieldName = strName;
}
/*****************************************************************************/
getSize()
{
	return this.vecSize;
}
/*****************************************************************************/
clear()
{
	this.grid = new Array(this.iNumSubdivs*this.iNumSubdivs*this.iNumVerticalSubdivs);
}
/*****************************************************************************/
addObject(vecPoint, pObject)
{
	let iIndex;
	if(vecPoint.z === undefined)
		iIndex = this.getIndexForPoint(vecPoint.x, this.vecMin.y, vecPoint.y);
	else
		iIndex = this.getIndexForPoint(vecPoint.x, vecPoint.y, vecPoint.z);

	this.internalAddToGridCell(iIndex, pObject);
}
/*****************************************************************************/
internalAddToGridCell(iIndex, pObject)
{
    if(this.grid[iIndex] === undefined)
        this.grid[iIndex] = [];

    if(this.grid[iIndex].indexOf(pObject) >= 0)
		return;

	if(!this.uniqueFieldName && pObject[VOXEL_UNIQUE_ID_FIELD] === undefined)
	{
		iVoxelIdCounter++;
		pObject[VOXEL_UNIQUE_ID_FIELD] = iVoxelIdCounter;
	}
    this.grid[iIndex].push(pObject);
}
/*****************************************************************************/
addLineSegment(vecStart, vecEnd, pObject)
{
	this.addBBox(vecStart, vecEnd, pObject);
}
/*****************************************************************************/
addBBox(vecStart, vecEnd, pObject)
{
	let vecAtOrigin;
	if(vecStart.z === undefined)
		vecAtOrigin = new BABYLON.Vector3(vecStart.x - this.vecMin.x, 0, vecStart.y - this.vecMin.z);
	else
		vecAtOrigin = vecStart.subtract(this.vecMin);

	let iStartX = CoreUtils.clamp(Math.floor(vecAtOrigin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	let iStartY = CoreUtils.clamp(Math.floor(vecAtOrigin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	let iStartZ = CoreUtils.clamp(Math.floor(vecAtOrigin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	if(vecEnd.z === undefined)
		vecAtOrigin = new BABYLON.Vector3(vecEnd.x - this.vecMin.x, 0, vecEnd.y - this.vecMin.z);
	else
		vecAtOrigin = vecEnd.subtract(this.vecMin);
	let iEndX = CoreUtils.clamp(Math.floor(vecAtOrigin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	let iEndY = CoreUtils.clamp(Math.floor(vecAtOrigin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	let iEndZ = CoreUtils.clamp(Math.floor(vecAtOrigin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	this.addObjectForVolumeInternal(iStartX, iStartY, iStartZ, iEndX, iEndY, iEndZ, pObject);
}
/*****************************************************************************/
addLine(iRawStartX, iRawStartY, iRawStartZ, iRawEndX, iRawEndY, iRawEndZ, pObject)
{
	let fDiff;

	fDiff = iRawStartX - this.vecMin.x;
	let iStartX = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	fDiff = iRawStartY - this.vecMin.y;
	let iStartY = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	fDiff = iRawStartZ - this.vecMin.z;
	let iStartZ = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	fDiff = iRawEndX - this.vecMin.x;
	let iEndX = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	fDiff = iRawEndY - this.vecMin.y;
	let iEndY = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	fDiff = iRawEndZ - this.vecMin.z;
	let iEndZ = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	this.addObjectForVolumeInternal(iStartX, iStartY, iStartZ, iEndX, iEndY, iEndZ, pObject);
}
/*****************************************************************************/
addLineSegmentPrecise(vecStartIn, vecEndIn, pObject)
{
	let vecStart = vecStartIn;
	let vecEnd = vecEndIn;

	if(vecStartIn.z === undefined)
		vecStart = new BABYLON.Vector3(vecStartIn.x, this.vecMin.y, vecStartIn.y);
	if(vecEndIn.z === undefined)
		vecEnd = new BABYLON.Vector3(vecEndIn.x, this.vecMin.y, vecEndIn.y);	

	let vecDir = vecEnd.subtract(vecStart);
	let fLength = vecDir.length();
	vecDir.normalize();

	let iLastIndexAdded = -1;
	let vecCurrPos = vecStart;
	for(let fCurrPos = 0; fCurrPos < fLength; )
	{	
		// Add us to the grid:
		let iCellIndex = this.getIndexForPoint(vecCurrPos.x, vecCurrPos.y, vecCurrPos.z);
		iLastIndexAdded = iCellIndex;

		this.internalAddToGridCell(iCellIndex, pObject);

		// Find the next set of voxel borders:
		let fMinDist = Number.MAX_VALUE;
		['x', 'y', 'z'].forEach((axis) => {

			if(Math.abs(vecDir[axis]) < 1e-4)
				return;

			let iGridIndex = (vecCurrPos[axis] - this.vecMin[axis])/this.vecBoxSize[axis];
			if(vecDir[axis] > 0)
				iGridIndex = Math.floor(iGridIndex) + 1;
			else if(vecDir[axis] < 0)
				iGridIndex = Math.ceil(iGridIndex) - 1;

			// Now, we need to find its position in space:
			let fNextAxisPos = iGridIndex*this.vecBoxSize[axis] + this.vecMin[axis];

			// Find the next intersection
			let t = (fNextAxisPos - vecStart[axis])/vecDir[axis] + this.vecBoxSize[axis]*0.01;
			if(t < fMinDist)
				fMinDist = t;
		});

		fCurrPos = fMinDist;
		if(fMinDist != Number.MAX_VALUE)
			vecCurrPos = vecStart.add(vecDir.scale(fMinDist));
	}

	// Make sure we add the last point:
	let iCellIndex = this.getIndexForPoint(vecEnd.x, vecEnd.y, vecEnd.z);
	if(iCellIndex != iLastIndexAdded)
		this.internalAddToGridCell(iCellIndex, pObject);
}
/*****************************************************************************/
addFace(vecPoints, pObject)
{
	let iMinVoxelX = Number.MAX_VALUE;
	let iMinVoxelY = Number.MAX_VALUE;
	let iMinVoxelZ = Number.MAX_VALUE;

	let iMaxVoxelX = -Number.MAX_VALUE;
	let iMaxVoxelY = -Number.MAX_VALUE;
	let iMaxVoxelZ = -Number.MAX_VALUE;

	let iCurrVoxelX;
	let iCurrVoxelY;
	let iCurrVoxelZ;

	for(let vecPoint of vecPoints)
	{
		let vecAtOrigin = vecPoint.subtract(this.vecMin);
		iCurrVoxelX = CoreUtils.clamp(Math.floor(vecAtOrigin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
		iCurrVoxelY = CoreUtils.clamp(Math.floor(vecAtOrigin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
		iCurrVoxelZ = CoreUtils.clamp(Math.floor(vecAtOrigin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

		if(iCurrVoxelX < iMinVoxelX)
			iMinVoxelX = iCurrVoxelX;
		if(iCurrVoxelY < iMinVoxelY)
			iMinVoxelY = iCurrVoxelY;
		if(iCurrVoxelZ < iMinVoxelZ)
			iMinVoxelZ = iCurrVoxelZ;

		if(iCurrVoxelX > iMaxVoxelX)
			iMaxVoxelX = iCurrVoxelX;
		if(iCurrVoxelY > iMaxVoxelY)
			iMaxVoxelY = iCurrVoxelY;
		if(iCurrVoxelZ > iMaxVoxelZ)
			iMaxVoxelZ = iCurrVoxelZ;
	}

	this.addObjectForVolumeInternal(iMinVoxelX, iMinVoxelY, iMinVoxelZ, iMaxVoxelX, iMaxVoxelY, iMaxVoxelZ, pObject);
}
/*****************************************************************************/
addObjectForVolumeInternal(iStartX, iStartY, iStartZ, iEndX, iEndY, iEndZ, pObject)
{
	let fTemp;
	if(iEndX < iStartX)
	{
		fTemp = iStartX;
		iStartX = iEndX;
		iEndX = fTemp;
	}
	if(iEndY < iStartY)
	{
		fTemp = iStartY;
		iStartY = iEndY;
		iEndY = fTemp;
	}
	if(iEndZ < iStartZ)
	{
		fTemp = iStartZ;
		iStartZ = iEndZ;
		iEndZ = fTemp;
	}

	if(!this.uniqueFieldName && pObject[VOXEL_UNIQUE_ID_FIELD] === undefined)
	{
		iVoxelIdCounter++;
		pObject[VOXEL_UNIQUE_ID_FIELD] = iVoxelIdCounter;
	}

	let iCellIndex;
	for(let iCurrX = iStartX; iCurrX <= iEndX; iCurrX++)
	{
		for(let iCurrY = iStartY; iCurrY <= iEndY; iCurrY++)
		{
			for(let iCurrZ = iStartZ; iCurrZ <= iEndZ; iCurrZ++)
			{
				iCellIndex = this.iNumSubdivs*this.iNumVerticalSubdivs*iCurrZ + this.iNumVerticalSubdivs*iCurrY + iCurrX;
				this.internalAddToGridCell(iCellIndex, pObject);
			}
		}
	}
}
/*****************************************************************************/
getObjects(vecPoint)
{
	let iIndex;
	if(vecPoint.z === undefined)
		iIndex = this.getIndexForPoint(vecPoint.x, this.vecMin.y, vecPoint.y);
	else
		iIndex = this.getIndexForPoint(vecPoint.x, vecPoint.y, vecPoint.z);

    if(this.grid[iIndex] === undefined)
        return [];

    return this.grid[iIndex];
}
/*****************************************************************************/
getObjectsNear(vecPoint, fDistance, bReturnUniquesOnly = true)
{
	if(vecPoint.z === undefined)
		vecPoint = new BABYLON.Vector3(vecPoint.x, this.vecMin.y, vecPoint.y);

	let vecStart = new BABYLON.Vector3(vecPoint.x - fDistance, vecPoint.y - fDistance, vecPoint.z - fDistance);
	let vecEnd = new BABYLON.Vector3(vecPoint.x + fDistance, vecPoint.y + fDistance, vecPoint.z + fDistance);

	return this.getObjectsForSegment(vecStart, vecEnd, bReturnUniquesOnly);
}
/*****************************************************************************/
getObjectsForSegment(vecStart, vecEnd, bReturnUniquesOnly = true)
{
	let vecAtOrigin = vecStart.subtract(this.vecMin);
	let iStartX = CoreUtils.clamp(Math.floor(vecAtOrigin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	let iStartY = CoreUtils.clamp(Math.floor(vecAtOrigin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	let iStartZ = CoreUtils.clamp(Math.floor(vecAtOrigin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	vecAtOrigin = vecEnd.subtract(this.vecMin);
	let iEndX = CoreUtils.clamp(Math.floor(vecAtOrigin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	let iEndY = CoreUtils.clamp(Math.floor(vecAtOrigin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	let iEndZ = CoreUtils.clamp(Math.floor(vecAtOrigin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

	let fTemp;
	if(iEndX < iStartX)
	{
		fTemp = iStartX;
		iStartX = iEndX;
		iEndX = fTemp;
	}
	if(iEndY < iStartY)
	{
		fTemp = iStartY;
		iStartY = iEndY;
		iEndY = fTemp;
	}
	if(iEndZ < iStartZ)
	{
		fTemp = iStartZ;
		iStartZ = iEndZ;
		iEndZ = fTemp;
	}

	let resList = [];
	let pUniquesSet = new Set();
	let iCellIndex;
	const uniqueFieldName = this.uniqueFieldName ? this.uniqueFieldName : VOXEL_UNIQUE_ID_FIELD;
	for(let iCurrX = iStartX; iCurrX <= iEndX; iCurrX++)
	{
		for(let iCurrY = iStartY; iCurrY <= iEndY; iCurrY++)
		{
			for(let iCurrZ = iStartZ; iCurrZ <= iEndZ; iCurrZ++)
			{
				iCellIndex = this.iNumSubdivs*this.iNumVerticalSubdivs*iCurrZ + this.iNumVerticalSubdivs*iCurrY + iCurrX;
				if(this.grid[iCellIndex] === undefined)
					continue;

				if(!bReturnUniquesOnly)
					resList = resList.concat(this.grid[iCellIndex]);
				else
				{
					for(let pObject of this.grid[iCellIndex])
					{
						if(!pUniquesSet.has(pObject[uniqueFieldName]))
						{
							pUniquesSet.add(pObject[uniqueFieldName]);
							resList.push(pObject);
						}
					}
				}
			}
		}
	}

	return resList;
}
/*****************************************************************************/
getObjectsForRay(vecStartIn, vecDirIn, bReturnUniquesOnly = true)
{
	let vecStart = vecStartIn;
	let vecDir = vecDirIn;

	if(vecStartIn.z === undefined)
		vecStart = new BABYLON.Vector3(vecStartIn.x, this.vecMin.y, vecStartIn.y);
	if(vecDir.z === undefined)
		vecDir = new BABYLON.Vector3(vecDir.x, 0, vecDirIn.y);

	// Find intersection
	let resList = [];
	let gridIntersectInfo = Utils3D.rayBoxIntersection(vecStart, vecDir, this.vecMin, this.vecMax);
	if(gridIntersectInfo === false)
		return resList;

	let pUniquesSet = new Set();
	let axes = ['x', 'y', 'z'];
	let iLastIndexAdded = -1;
	let fCurrPos = gridIntersectInfo.min;
	const uniqueFieldName = this.uniqueFieldName ? this.uniqueFieldName : VOXEL_UNIQUE_ID_FIELD;
	let vecCurrPos = vecStart.add(vecDir.scale(fCurrPos));
	for(; fCurrPos <= gridIntersectInfo.max; )
	{	
		let iCellIndex = this.getIndexForPoint(vecCurrPos.x, vecCurrPos.y, vecCurrPos.z);
		if(iCellIndex !== iLastIndexAdded)
		{
			iLastIndexAdded = iCellIndex;

			if(this.grid[iCellIndex])
			{
				if(!bReturnUniquesOnly)
					resList = resList.concat(this.grid[iCellIndex]);
				else
				{
					for(let pObject of this.grid[iCellIndex])
					{
						if(!pUniquesSet.has(pObject[uniqueFieldName]))
						{
							pUniquesSet.add(pObject[uniqueFieldName]);
							resList.push(pObject);
						}
					}
				}
			}
		}

		// Find the next set of voxel borders:
		let fMinDist = Number.MAX_VALUE;
		for(let axis of axes)
		{
			if(Math.abs(vecDir[axis]) < 1e-4)
				continue;

			let iGridIndex = (vecCurrPos[axis] - this.vecMin[axis])/this.vecBoxSize[axis];
			if(vecDir[axis] > 0)
				iGridIndex = Math.floor(iGridIndex) + 1;
			else if(vecDir[axis] < 0)
				iGridIndex = Math.ceil(iGridIndex) - 1;

			// Now, we need to find its position in space:
			let fNextAxisPos = iGridIndex*this.vecBoxSize[axis] + this.vecMin[axis];

			// Find the next intersection
			let t = (fNextAxisPos - vecStart[axis])/vecDir[axis] + this.vecBoxSize[axis]*0.01;
			if(t < fMinDist)
				fMinDist = t;
		}

		fCurrPos = fMinDist;
		if(fMinDist != Number.MAX_VALUE)
			vecCurrPos = vecStart.add(vecDir.scale(fMinDist));
		else
			break;
	}

	return resList;
}
/*****************************************************************************/
getIndexForPoint(iRawX, iRawY, iRawZ)
{
	let fDiff;

	fDiff = iRawX - this.vecMin.x;
    let iX = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
	fDiff = iRawY - this.vecMin.y;
    let iY = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
	fDiff = iRawZ - this.vecMin.z;
    let iZ = CoreUtils.clamp(Math.floor(fDiff/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

    return this.iNumSubdivs*this.iNumVerticalSubdivs*iZ + this.iNumVerticalSubdivs*iY + iX;
}
/*****************************************************************************/
getIndexForCellAt(iRawX, iRawY, iRawZ)
{
	return this.iNumSubdivs*this.iNumVerticalSubdivs*iRawZ + this.iNumVerticalSubdivs*iRawY + iRawX;
}
/*****************************************************************************/
getNumSubdivs()
{
	return this.iNumSubdivs;
}
/*****************************************************************************/
getCellCenter(iRawX, iRawY, iRawZ)
{
	return new BABYLON.Vector3(
			(iRawX + 0.5)*this.vecBoxSize.x + this.vecMin.x,
			(iRawY + 0.5)*this.vecBoxSize.y + this.vecMin.y,
			(iRawZ + 0.5)*this.vecBoxSize.z + this.vecMin.z,
		);	
}
/*****************************************************************************/
iterateOverVoxels(pCallback)
{
	let iCellIndex;
	for(let iCurrX = 0; iCurrX < this.iNumSubdivs; iCurrX++)
	{
		for(let iCurrY = 0; iCurrY < this.iNumVerticalSubdivs; iCurrY++)
		{
			for(let iCurrZ = 0; iCurrZ < this.iNumSubdivs; iCurrZ++)
			{
				iCellIndex = this.iNumSubdivs*this.iNumVerticalSubdivs*iCurrZ + this.iNumVerticalSubdivs*iCurrY + iCurrX;
				pCallback(iCurrX, iCurrY, iCurrZ, this.grid[iCellIndex]);
			}
		}
	}
}
/*****************************************************************************/
removeObject(object, optBBoxMin, optBBoxMax)
{
	if(optBBoxMin && optBBoxMax)
	{
		let iMinVoxelX = CoreUtils.clamp(Math.floor(optBBoxMin.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
		let iMinVoxelY = CoreUtils.clamp(Math.floor(optBBoxMin.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
		let iMinVoxelZ = CoreUtils.clamp(Math.floor(optBBoxMin.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

		let iMaxVoxelX = CoreUtils.clamp(Math.floor(optBBoxMax.x/this.vecBoxSize.x), 0, this.iNumSubdivs - 1);
		let iMaxVoxelY = CoreUtils.clamp(Math.floor(optBBoxMax.y/this.vecBoxSize.y), 0, this.iNumVerticalSubdivs - 1);
		let iMaxVoxelZ = CoreUtils.clamp(Math.floor(optBBoxMax.z/this.vecBoxSize.z), 0, this.iNumSubdivs - 1);

		let iCellIndex;
		for(let iCurrX = iMinVoxelX; iCurrX <= iMaxVoxelX; iCurrX++)
		{
			for(let iCurrY = iMinVoxelY; iCurrY <= iMaxVoxelY; iCurrY++)
			{
				for(let iCurrZ = iMinVoxelZ; iCurrZ <= iMaxVoxelZ; iCurrZ++)
				{
					iCellIndex = this.iNumSubdivs*this.iNumVerticalSubdivs*iCurrZ + this.iNumVerticalSubdivs*iCurrY + iCurrX;

					if(!this.grid[iCellIndex])
						continue;

					let objIndex = this.grid[iCellIndex].indexOf(object);
					if(objIndex >= 0)
						this.grid[iCellIndex].splice(objIndex, 1);
				}
			}
		}
	}
	else
	{
		for(let idx = 0; idx < this.grid.length; idx++)
		{
			if(!this.grid[idx])
				continue;

			let objIndex = this.grid[idx].indexOf(object);
			if(objIndex >= 0)
				this.grid[idx].splice(objIndex, 1);
		}
	}
}

/*****************************************************************************/
removeObjectsOfType(objectType)
{

		for(let cellIdx = 0; cellIdx < this.grid.length; cellIdx++)
		{
			const gridCell = this.grid[cellIdx];
			if(!gridCell)
				continue;

			for(let objIdx = gridCell.length - 1; objIdx >= 0; objIdx--) {
				let obj = gridCell[objIdx];
				if(obj.getType && obj.getType() == objectType) {
					gridCell.splice(objIdx, 1);
				}
			}
		}
	}
/*****************************************************************************/
}
